
import { Directive, Inject, Input, OnDestroy, OnInit, Optional, Renderer2, ElementRef } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Directive({
  selector: '[appDirByLang]'
})
export class DirByLangDirective implements OnInit, OnDestroy {
  /** Optional: override RTL list (ISO codes). Default: he, ar, fa, ur */
  @Input('appDirByLangRtl') rtlLangs: string[] = ['he', 'ar', 'fa', 'ur'];

  /** Optional: apply direction to 'host' (default), 'html', or 'body' */
  @Input('appDirByLangTarget') target: 'host' | 'html' | 'body' = 'host';

  private sub?: Subscription;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    // Apply initial direction
    this.applyDir(this.translate.currentLang || this.translate.getDefaultLang()||"en");

    // React to language changes
    this.sub = this.translate.onLangChange.subscribe((e: LangChangeEvent) => {
      this.applyDir(e.lang);
    });
  }

  private applyDir(lang?: string): void {
    const isRtl = this.isRtlLang(lang);
    const dir = isRtl ? 'rtl' : 'ltr';

    const targetEl =
      this.target === 'html'
        ? this.document.documentElement
        : this.target === 'body'
        ? this.document.body
        : this.el.nativeElement;

    this.renderer.setAttribute(targetEl, 'dir', dir);

    // Optional: add a helper class for CSS tweaks
    this.toggleClass(targetEl, 'dir-rtl', isRtl);
    this.toggleClass(targetEl, 'dir-ltr', !isRtl);

    // Optional: set lang attribute (useful for accessibility)
    if (lang) {
      this.renderer.setAttribute(targetEl, 'lang', lang);
    }
  }

  private toggleClass(el: Element, cls: string, on: boolean): void {
    if (on) {
      this.renderer.addClass(el, cls);
    } else {
      this.renderer.removeClass(el, cls);
    }
  }

  private isRtlLang(lang?: string): boolean {
    if (!lang) return false;
    const base = lang.split('-')[0].toLowerCase(); // e.g., 'he-IL' -> 'he'
    return this.rtlLangs.map(l => l.toLowerCase()).includes(base);
    // You can also extend with explicit script checks if needed.
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
